<template>
    <div>
        <b-nav-item-dropdown v-if="appointmentStatus">
            <template slot="button-content">
                <i class="cus-date"></i>Appointment
            </template>
            <b-dropdown-item>
                <i class="cus-date-error"></i>Booked<br />
                <p class="appointment-dropdown-description">
                    <i>{{ appointmentStatuses.booked.description }} </i>
                </p>
            </b-dropdown-item>
            <li class="dropdown-divider"></li>
            <b-dropdown-item
                @click="changeAppointmentStatus(appointmentStatuses.checkIn)"
            >
                <i class="cus-accept"></i
                ><span
                    :class="
                        allowStatusChange(appointmentStatuses.checkIn)
                            ? boldText
                            : ''
                    "
                    >{{ appointmentStatuses.checkIn.label }}</span
                ><br />
                <p class="appointment-dropdown-description">
                    <i>{{ appointmentStatuses.checkIn.description }}</i>
                </p>
            </b-dropdown-item>
            <li class="dropdown-divider"></li>
            <b-dropdown-item
                @click="changeAppointmentStatus(appointmentStatuses.inRoom)"
            >
                <i class="cus-door"></i
                ><span
                    :class="
                        allowStatusChange(appointmentStatuses.inRoom)
                            ? boldText
                            : ''
                    "
                    >{{ appointmentStatuses.inRoom.label }}</span
                ><br />
                <p
                    v-if="appointmentStatuses.inRoom.description"
                    class="appointment-dropdown-description"
                >
                    <i>{{ appointmentStatuses.inRoom.description }} </i>
                </p>
            </b-dropdown-item>
            <li class="dropdown-divider"></li>
            <b-dropdown-item
                @click="
                    changeAppointmentStatus(appointmentStatuses.visitComplete)
                "
            >
                <i class="cus-folder-heart"></i
                ><span
                    :class="
                        allowStatusChange(appointmentStatuses.visitComplete)
                            ? boldText
                            : ''
                    "
                    >{{ appointmentStatuses.visitComplete.label }}</span
                ><br />
                <p
                    v-if="appointmentStatuses.visitComplete.description"
                    class="appointment-dropdown-description"
                >
                    <i>{{ appointmentStatuses.visitComplete.description }} </i>
                </p>
            </b-dropdown-item>
            <li class="dropdown-divider"></li>
            <b-dropdown-item
                :disabled="isHasUsersChooseInvoice"
                @click="changeAppointmentStatus(appointmentStatuses.checkOut)"
            >
                <i class="cus-door-out"></i
                ><span
                    :class="
                        allowStatusChange(appointmentStatuses.checkOut)
                            ? boldText
                            : ''
                    "
                    >{{ appointmentStatuses.checkOut.label }}</span
                ><br />
                <p
                    v-if="appointmentStatuses.checkOut.description"
                    class="appointment-dropdown-description"
                >
                    <i>{{ appointmentStatuses.checkOut.description }} </i>
                </p>
            </b-dropdown-item>
        </b-nav-item-dropdown>
        <status
            modal-id="modal-status-window"
            ref="statusWindow"
            :invoiceId="invoiceId"
        />
    </div>
</template>

<script>
import ClientAppointmentServices from "@/services/client-appointments";
import ScheduleHelper from "@/module/schedule/helper";
import { forEach, isEmpty } from "lodash";

/**
 * @name AppointmnentStatusDropdown
 * @description Uses in invoice page and estimate page
 * @see apps/application/views/staff/billing/invoice/invoice/navbar.php
 */
export default {
    mixins: [ScheduleHelper],
    components: {
        Status: () => import("@/module/schedule/appointment/status.vue")
    },
    props: {
        isInvoice: {
            type: Boolean,
            default: true
        },
        invoiceId: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            boldText: "bold-text",
            appointmentStatuses: {
                booked: { label: "Booked", description: "", id: 1, index: 1 },
                checkIn: {
                    label: "Checked In",
                    description: "",
                    id: 4,
                    index: 4
                },
                inRoom: { label: "In Room", description: "", id: 5, index: 5 },
                visitComplete: {
                    label: "Visit Complete",
                    description: "",
                    id: 6,
                    index: 6
                },
                checkOut: {
                    label: "Check Out",
                    description: "",
                    id: 7,
                    index: 7
                }
            },
            appointmentStatus: null
        };
    },
    computed: {
        invoiceDetails() {
            return this.$store.getters["billing/invoiceDetails"];
        },
        estimateDetails() {
            return this.$store.getters["billing/estimateDetails"];
        },
        isSoap() {
            return this.$route.name === "soap";
        },
        soapDetails() {
            return this.$store.getters["soap/soapDetails"];
        },
        isHasUsersChooseInvoice() {
            return this.invoiceDetails
                ? !isEmpty(
                      this.$store.getters["billing/findUserChooseInvoice"](
                          this.invoiceDetails.id
                      )
                  )
                : false;
        }
    },
    watch: {
        "$store.state.billing.invoiceDetails": {
            handler(details) {
                if (!details) {
                    return;
                }
                if (!details.appointment) {
                    return;
                }

                this.prepareAppointment();
            },
            // force eager callback execution
            immediate: true
        },
        "$store.state.billing.estimateDetails": {
            handler(details) {
                if (!details) {
                    return;
                }
                if (!details.appointment) {
                    return;
                }

                this.prepareAppointment();
            },
            // force eager callback execution
            immediate: true
        },
        "$store.state.soap.soapDetails": {
            handler(details) {
                if (!details) {
                    return;
                }
                if (!details.appointment) {
                    return;
                }

                this.prepareAppointment();
            },
            // force eager callback execution
            immediate: true
        }
    },
    methods: {
        refetch() {
            this.$emit("reload-page");
        },
        async prepareAppointment() {
            let self = this;
            let id = null;
            if (this.isSoap) {
                id = this.soapDetails.appointment_id;
            } else {
                id = this.isInvoice
                    ? this.invoiceDetails.appointment.id
                    : this.estimateDetails.appointment.id;
            }

            if (!id) {
                console.log("Something wrong with an ID.");
                return;
            }
            let appointment = await ClientAppointmentServices.getHistory(id);

            // code extracted directly from CI with minor changed
            forEach(appointment.resources.reverse(), function (data) {
                if (data.status_id == "") {
                    self.appointmentStatuses.booked.description =
                        data.update_at + " by " + data.update_by;
                } else {
                    // add description for status less than current status
                    forEach(self.appointmentStatuses, function (value, key) {
                        if (data.status_id === value.id) {
                            self.appointmentStatuses[key]["description"] =
                                data.update_at + " by " + data.update_by;
                        }
                    });
                }
            });

            await this._getAppointment(id, { with_cancelled: true }).then(
                resource => {
                    self.appointmentStatus = resource.appointmentStatus.index;
                }
            );

            if (self.appointmentStatus == this.APPOINTMENT.STATUS_IN_ROOM) {
                // removed description for appointment status VISIT COMPLETE if current status is IN ROOM

                self.appointmentStatuses.visitComplete.description = "";
            } else if (
                self.appointmentStatus == this.APPOINTMENT.STATUS_CHECKED_IN
            ) {
                // removed description for appointment status VISIT COMPLETE and status IN ROOM if current status is CHECKED IN
                self.appointmentStatuses.inRoom.description = "";
                self.appointmentStatuses.visitComplete.description = "";
            } else if (
                self.appointmentStatus == this.APPOINTMENT.STATUS_CHECKED_OUT
            ) {
                // show label check out in past tense if already checked out
                self.appointmentStatuses.checkOut.label = "Checked Out";
            }
        },
        changeAppointmentStatus(status) {
            let allowChange = this.allowStatusChange(status);

            if (!allowChange) return;

            let appointmentId = null;
            if (this.isSoap) {
                appointmentId = this.soapDetails.appointment_id;
            } else {
                appointmentId = this.isInvoice
                    ? this.invoiceDetails.appointment.id
                    : this.estimateDetails.appointment.id;
            }
            this.$refs.statusWindow.open({
                id: appointmentId,
                preSelectStatus: status.id
            });
        },
        allowStatusChange(status) {
            /**
             * @method allowStatusChange
             * @description this function is use to BOLD TEXT and TRIGGER MODAL.
             * @returns {Boolean}
             */

            let statusId = status.id;
            switch (this.appointmentStatus) {
                case this.APPOINTMENT.STATUS_CHECKED_IN:
                    if (statusId == this.APPOINTMENT.STATUS_CHECKED_IN)
                        return false;
                    return true;
                case this.APPOINTMENT.STATUS_IN_ROOM:
                    if (statusId == this.APPOINTMENT.STATUS_CHECKED_IN)
                        return false;
                    return true;
                case this.APPOINTMENT.STATUS_VISIT_COMPLETE:
                    if (statusId == this.APPOINTMENT.STATUS_VISIT_COMPLETE)
                        return false;
                    return true;
                case this.APPOINTMENT.STATUS_CHECKED_OUT:
                    return false;
            }
            return false;
        }
    }
};
</script>
<style scoped>
.appointment-dropdown-description {
    font-weight: 100;
    padding-left: 1.6em;
}

.bold-text {
    font-weight: bolder;
    cursor: pointer;
    color: #7a0000;
    text-decoration: none;
    background-color: transparent;
}

.bold-text:hover {
    color: inherit;
}
</style>
